/**
 * RequestModal.tsx
 * 
 * Modal per la creazione e modifica rapida delle richieste nell'applicazione Ufficio Stile.
 * Componente compatto che include form principale, selezione cliente e articoli base.
 * Ottimizzato per workflow veloce con validazione real-time e UX fluida.
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  TextInput,
  Textarea,
  Select,
  Card,
  Badge,
  Alert,
  Stepper,
  Grid,
  NumberInput,
  Accordion,
  Divider
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { z } from 'zod';
import {
  IconCheck,
  IconX,
  IconUser,
  IconFileText,
  IconPackage,
  IconCalendar,
  IconAlertCircle,
  IconHash,
  IconRefresh
} from '@tabler/icons-react';
import { useRequests, useProgressiveNumberForm } from '../../../hooks';
import { hasClosureDate, isInPreCampionaturaPhase } from '../../../utils/request-status-utils';
import {
  Request,
  RequestCreateRequest,
  RequestUpdateRequest,
  RequestStatus,
  RequestPriority,
  RequestType
} from '../../../types/api';
import { toDateOnlyISOString } from '../../../utils/date-utils';
import { ClientSelector } from './ClientSelector';
import { ArticleSelector } from './ArticleSelector';
import { ExcelArticleImport } from './ExcelArticleImport';

interface RequestModalProps {
  opened: boolean;
  onClose: () => void;
  request?: Request; // Se presente, è modalità edit
  mode?: 'create' | 'edit';
  onSuccess?: (request: Request) => void;
  defaultRequestType?: string; // Tipo di richiesta preselezionato
}

/**
 * Helper per convertire il valore requestType (stringa enum dal backend o numero)
 * nel valore numerico-stringa usato dal Select ('1', '2', '3', '4').
 */
const getRequestTypeValue = (requestType: any): string => {
  if (!requestType) return '1';
  // Se è già un numero o una stringa numerica
  const num = Number(requestType);
  if (!isNaN(num) && num >= 1 && num <= 4) return String(num);
  // Se è un nome enum (stringa dal backend, es. "Bozzetto")
  const enumMap: Record<string, string> = {
    'Bozzetto': '1', 'Campionatura': '2', 'Produzione': '3', 'Gamma': '4'
  };
  return enumMap[requestType] || '1';
};

interface FormValues {
  title: string;
  clientName: string;
  requestType: string;
  deliveryDate: Date | null;
  notes: string;
  clientId: string;
  articles: Array<{
    articleCode: string;
    articleName: string;
    quantity: number;
    notes?: string;
    workTypes?: string;
  }>;
  tags?: string;
  estimatedHours?: number;
  dueDate?: Date | null;
  // Progressive Number
  progressiveNumber: number | null;
  progressiveYear: number;
  // Data creazione (solo in edit)
  createdAt: Date | null;
  // Data completamento (solo in edit, se status = Completed)
  completedAt: Date | null;
  // Data fine Pre-Campionatura (solo in edit, se già valorizzata: correzione manuale)
  preCampionaturaCompletedAt: Date | null;
}

const requestSchema = z.object({
  title: z.string().min(1, 'Il titolo è obbligatorio'),
  clientName: z.string().min(1, 'Il cliente è obbligatorio'),
  requestType: z.string().min(1, 'Il tipo di richiesta è obbligatorio'),
  deliveryDate: z.date().nullable(),
  notes: z.string().optional(),
  clientId: z.string().optional(),
  articles: z.array(z.object({
    articleCode: z.string().min(1, 'Codice articolo obbligatorio'),
    articleName: z.string().min(1, 'Nome articolo obbligatorio'),
    quantity: z.number().min(1, 'Quantità deve essere almeno 1'),
    notes: z.string().optional(),
    workTypes: z.string().optional()
  })).min(1, 'Almeno un articolo è richiesto'),
  tags: z.string().optional(),
  estimatedHours: z.number().optional(),
  dueDate: z.date().nullable().optional(),
  progressiveNumber: z.number().nullable(),
  progressiveYear: z.number().min(2020).max(2100),
  createdAt: z.date().nullable(),
  completedAt: z.date().nullable(),
  preCampionaturaCompletedAt: z.date().nullable(),
});

export function RequestModal({ opened, onClose, request, mode = 'create', onSuccess, defaultRequestType }: RequestModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedClient, setSelectedClient] = useState<{clientId?: string; clientName: string} | null>(null);
  const [progressiveNumberError, setProgressiveNumberError] = useState<string | null>(null);

  const { createMutation, updateMutation } = useRequests();

  const currentYear = new Date().getFullYear();
  const initialRequestType = request?.requestType ? Number(getRequestTypeValue(request.requestType)) : (defaultRequestType ? Number(defaultRequestType) : 1);

  const form = useForm<FormValues>({
    initialValues: {
      title: request?.title || '',
      clientName: request?.clientName || '',
      requestType: request?.requestType ? getRequestTypeValue(request.requestType) : (defaultRequestType || '1'),
      deliveryDate: request?.deliveryDate ? new Date(request.deliveryDate) : null,
      notes: request?.notes || '',
      clientId: request?.clientId || '',
      articles: request?.articles?.map(article => ({
        articleCode: article.articleCode,
        articleName: article.articleName,
        quantity: article.quantity,
        notes: article.notes,
        workTypes: article.workTypes
      })) || [],
      tags: '',
      estimatedHours: 0,
      dueDate: null,
      progressiveNumber: request?.progressiveNumber ?? null,
      progressiveYear: request?.progressiveYear ?? currentYear,
      createdAt: request?.createdAt ? new Date(request.createdAt) : null,
      completedAt: request?.completedAt ? new Date(request.completedAt) : null,
      preCampionaturaCompletedAt: request?.preCampionaturaCompletedAt ? new Date(request.preCampionaturaCompletedAt) : null,
    },
    validate: zodResolver(requestSchema)
  });

  // La richiesta è in una fase di Pre-Campionatura: il tipo non è modificabile (solo Campionatura)
  const isRequestInPreCampionatura = mode === 'edit' && isInPreCampionaturaPhase(request?.status);

  // Hook per il numero progressivo
  // Validazione: evita di passare NaN se requestType è vuoto o non valido
  const requestTypeValue = form.values.requestType;
  const requestTypeForHook = requestTypeValue && requestTypeValue !== ''
    ? Number(requestTypeValue) as RequestType
    : undefined;

  const {
    suggestion,
    isLoadingSuggestion,
    refetchSuggestion,
    validateNumber,
    isValidating
  } = useProgressiveNumberForm(
    requestTypeForHook,  // Passa undefined invece di NaN se non valido
    mode === 'edit' ? Number(request?.id) : undefined
  );

  // Aggiorna il suggerimento quando cambia il tipo di richiesta
  useEffect(() => {
    if (mode === 'create' && suggestion && form.values.progressiveNumber === null) {
      form.setFieldValue('progressiveNumber', suggestion.number);
      form.setFieldValue('progressiveYear', suggestion.year);
    }
  }, [suggestion, mode]);

  // Valida il numero progressivo al blur
  const handleProgressiveNumberBlur = async () => {
    if (form.values.progressiveNumber === null) return;

    const result = await validateNumber(
      form.values.progressiveNumber,
      form.values.progressiveYear
    );

    if (result && !result.isAvailable) {
      setProgressiveNumberError(result.errorMessage || 'Numero progressivo non disponibile');
    } else {
      setProgressiveNumberError(null);
    }
  };

  // Reset form quando si chiude
  const handleClose = () => {
    form.reset();
    setCurrentStep(0);
    setSelectedClient(null);
    setProgressiveNumberError(null);
    onClose();
  };

  // Gestione submit
  const handleSubmit = async (values: FormValues) => {
    // Verifica errori numero progressivo prima di procedere
    if (progressiveNumberError) {
      notifications.show({
        title: 'Errore',
        message: progressiveNumberError,
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
      return;
    }

    try {
      if (mode === 'create') {
        const createData: RequestCreateRequest = {
          title: values.title,
          clientName: values.clientName,
          ...(values.clientId ? { clientId: values.clientId } : {}),
          requestType: Number(values.requestType),
          deliveryDate: values.dueDate ? toDateOnlyISOString(values.dueDate) : values.deliveryDate ? toDateOnlyISOString(values.deliveryDate) : undefined,
          notes: [
            values.notes,
            values.tags ? `Tags: ${values.tags}` : null,
            values.estimatedHours ? `Ore stimate: ${values.estimatedHours}` : null
          ].filter(Boolean).join('\n') || undefined,
          articles: values.articles.map(article => ({
            articleCode: article.articleCode,
            articleName: article.articleName,
            quantity: article.quantity,
            ...(article.notes ? { notes: article.notes } : {}),
            ...(article.workTypes ? { workTypes: article.workTypes } : {})
          })),
          // Progressive Number
          progressiveNumber: values.progressiveNumber ?? undefined,
          progressiveYear: values.progressiveYear
        };

        const newRequest = await createMutation.mutateAsync(createData);

        notifications.show({
          title: 'Richiesta creata',
          message: 'La richiesta è stata creata con successo',
          color: 'green',
          icon: <IconCheck size={16} />
        });

        onSuccess?.(newRequest);
      } else if (request) {
        const updateData: RequestUpdateRequest = {
          title: values.title,
          clientName: values.clientName,
          ...(values.clientId ? { clientId: values.clientId } : {}),
          requestType: Number(values.requestType),
          deliveryDate: values.dueDate ? toDateOnlyISOString(values.dueDate) : values.deliveryDate ? toDateOnlyISOString(values.deliveryDate) : undefined,
          notes: [
            values.notes,
            values.tags ? `Tags: ${values.tags}` : null,
            values.estimatedHours ? `Ore stimate: ${values.estimatedHours}` : null
          ].filter(Boolean).join('\n') || undefined,
          articles: values.articles.map(article => ({
            articleCode: article.articleCode,
            articleName: article.articleName,
            quantity: article.quantity,
            ...(article.notes ? { notes: article.notes } : {}),
            ...(article.workTypes ? { workTypes: article.workTypes } : {})
          })),
          // Progressive Number
          progressiveNumber: values.progressiveNumber ?? undefined,
          progressiveYear: values.progressiveYear,
          // Data creazione
          createdAt: values.createdAt ? toDateOnlyISOString(values.createdAt) : undefined,
          // Data completamento
          ...(values.completedAt ? { completedAt: toDateOnlyISOString(values.completedAt) } : {}),
          // Data fine Pre-Campionatura (correzione manuale)
          ...(values.preCampionaturaCompletedAt ? { preCampionaturaCompletedAt: toDateOnlyISOString(values.preCampionaturaCompletedAt) } : {}),
        };

        const updatedRequest = await updateMutation.mutateAsync({
          id: request.id,
          data: updateData
        });

        notifications.show({
          title: 'Richiesta aggiornata',
          message: 'La richiesta è stata aggiornata con successo',
          color: 'green',
          icon: <IconCheck size={16} />
        });

        onSuccess?.(updatedRequest);
      }

      handleClose();
    } catch (error) {
      notifications.show({
        title: 'Errore',
        message: `Errore durante ${mode === 'create' ? 'la creazione' : "l'aggiornamento"} della richiesta`,
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    }
  };

  // Gestione selezione cliente
  const handleClientSelect = (client: {clientId?: string; clientName: string}) => {
    setSelectedClient(client);
    form.setFieldValue('clientId', client.clientId || '');
    form.setFieldValue('clientName', client.clientName);
  };

  // Gestione aggiunta articolo
  const handleArticleAdd = (article: {
    articleCode: string;
    articleName?: string;
    quantity: number;
    unitPrice?: number;
    notes?: string;
    workTypes?: string;
    [key: string]: any;
  }) => {
    const currentArticles = form.values.articles;
    // Se manca articleName, prova a popolarlo con articleCode o placeholder
    const name = article.articleName && article.articleName.trim() !== ''
      ? article.articleName
      : article.articleCode || 'Articolo senza nome';
    form.setFieldValue('articles', [
      ...currentArticles,
      {
        ...article,
        articleName: name
      }
    ]);
  };

  // Rimozione articolo
  const handleArticleRemove = (index: number) => {
    const currentArticles = form.values.articles;
    form.setFieldValue('articles', currentArticles.filter((_, i) => i !== index));
  };

  // Import articoli da Excel (merge con esistenti)
  const handleExcelImport = (importedArticles: Array<{
    articleCode: string;
    articleName: string;
    quantity: number;
    notes?: string;
  }>) => {
    const currentArticles = form.values.articles;
    const newArticles = importedArticles.map(a => ({
      articleCode: a.articleCode,
      articleName: a.articleName,
      quantity: a.quantity,
      notes: a.notes
    }));
    form.setFieldValue('articles', [...currentArticles, ...newArticles]);
  };

  // Navigazione steps

  // Validazione step-by-step
  const nextStep = () => {
    const result = form.validate();
    // Solo se non ci sono errori per i campi dello step corrente, vai avanti
    let stepValid = true;
    if (currentStep === 0) {
      stepValid = !result.errors.title && !result.errors.requestType;
    } else if (currentStep === 1) {
      stepValid = !result.errors.clientName;
    } else if (currentStep === 2) {
      stepValid = !result.errors.articles;
    }
    if (stepValid) {
      setCurrentStep(current => current + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(current => current - 1);
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 0: // Dati base
        return form.values.title.length >= 3;
      case 1: // Cliente
        return form.values.clientName.length >= 3;
      case 2: // Articoli
        return form.values.articles.length > 0;
      default:
        return true;
    }
  };

  // Mostra errori principali in un Alert nello step corrente
  const renderStepErrors = () => {
    const errors = form.errors;
    let messages: string[] = [];
    if (currentStep === 0) {
      if (errors.title) messages.push(String(errors.title));
      if (errors.requestType) messages.push(String(errors.requestType));
    } else if (currentStep === 1) {
      if (errors.clientName) messages.push(String(errors.clientName));
    } else if (currentStep === 2) {
      if (errors.articles) messages.push(String(errors.articles));
    }
    if (messages.length === 0) return null;
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        {messages.map((msg, i) => <div key={i}>{msg}</div>)}
      </Alert>
    );
  };

  // Render del form diretto per modalità edit
  const renderEditForm = () => (
    <Stack gap="md">
      {/* Numero Progressivo - in evidenza */}
      <Card withBorder padding="md" bg="blue.0">
        <Text size="sm" fw={600} mb="sm">
          <Group gap="xs">
            <IconHash size={18} />
            Numero Progressivo
          </Group>
        </Text>
        <Grid align="flex-end">
          <Grid.Col span={5}>
            <NumberInput
              label="Numero"
              placeholder={isLoadingSuggestion ? 'Caricamento...' : 'N'}
              min={1}
              value={form.values.progressiveNumber ?? undefined}
              onChange={(value) => {
                form.setFieldValue('progressiveNumber', value as number ?? null);
                setProgressiveNumberError(null);
              }}
              onBlur={handleProgressiveNumberBlur}
              error={progressiveNumberError}
              rightSection={isValidating ? <IconRefresh size={14} className="animate-spin" /> : null}
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={1}>
            <Text size="xl" ta="center" pb="xs">/</Text>
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput
              label="Anno"
              placeholder="AAAA"
              min={2020}
              max={2100}
              {...form.getInputProps('progressiveYear')}
              onChange={(value) => {
                form.setFieldValue('progressiveYear', value as number);
                setProgressiveNumberError(null);
              }}
              onBlur={handleProgressiveNumberBlur}
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <Button
              variant="subtle"
              size="sm"
              onClick={() => {
                refetchSuggestion();
                if (suggestion) {
                  form.setFieldValue('progressiveNumber', suggestion.number);
                  form.setFieldValue('progressiveYear', suggestion.year);
                  setProgressiveNumberError(null);
                }
              }}
              loading={isLoadingSuggestion}
              leftSection={<IconRefresh size={14} />}
            >
              Auto
            </Button>
          </Grid.Col>
        </Grid>
        {progressiveNumberError && (
          <Alert color="red" variant="light" mt="xs" p="xs">
            <Text size="xs">{progressiveNumberError}</Text>
          </Alert>
        )}
      </Card>

      <Divider />

      {/* Dati principali */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Titolo"
            placeholder="Inserisci il titolo della richiesta..."
            required
            {...form.getInputProps('title')}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select
            label="Tipo Richiesta"
            data={[
              { value: '1', label: 'Bozzetto' },
              { value: '2', label: 'Campione' },
              { value: '3', label: 'Produzione' },
              { value: '4', label: 'Gamma' }
            ]}
            disabled={isRequestInPreCampionatura}
            description={isRequestInPreCampionatura ? 'Non modificabile durante la Pre-Campionatura' : undefined}
            {...form.getInputProps('requestType')}
            onChange={(value) => {
              form.setFieldValue('requestType', value || '1');
              // Reset progressive number quando cambia il tipo
              form.setFieldValue('progressiveNumber', null);
              setProgressiveNumberError(null);
              setTimeout(() => refetchSuggestion(), 100);
            }}
          />
        </Grid.Col>
      </Grid>

      {/* Cliente e Date */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Cliente"
            placeholder="Nome cliente..."
            required
            {...form.getInputProps('clientName')}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <DatePickerInput
            label="Data Scadenza"
            placeholder="Seleziona data..."
            {...form.getInputProps('deliveryDate')}
          />
        </Grid.Col>
      </Grid>

      {/* Data Creazione (solo in edit) */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <DatePickerInput
            label="Data Creazione"
            placeholder="Seleziona data..."
            leftSection={<IconCalendar size={16} />}
            {...form.getInputProps('createdAt')}
          />
        </Grid.Col>
        {/* Data di Chiusura (richieste completate o Gamma sospese) */}
        {hasClosureDate(request?.status, request?.requestType) && (
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <DatePickerInput
              label="Data di Chiusura"
              placeholder="Seleziona data..."
              leftSection={<IconCalendar size={16} />}
              clearable
              {...form.getInputProps('completedAt')}
            />
          </Grid.Col>
        )}
        {/* Data fine Pre-Campionatura (correzione manuale, solo se già valorizzata) */}
        {!!request?.preCampionaturaCompletedAt && (
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <DatePickerInput
              label="Data fine Pre-Campionatura"
              placeholder="Seleziona data..."
              leftSection={<IconCalendar size={16} />}
              minDate={request?.createdAt ? new Date(request.createdAt) : undefined}
              {...form.getInputProps('preCampionaturaCompletedAt')}
            />
          </Grid.Col>
        )}
      </Grid>

      {/* Note */}
      <Textarea
        label="Note"
        placeholder="Note aggiuntive..."
        minRows={3}
        {...form.getInputProps('notes')}
      />

      {/* Articoli (collapsible) */}
      <Accordion variant="contained" defaultValue="articles">
        <Accordion.Item value="articles">
          <Accordion.Control icon={<IconPackage size={18} />}>
            <Group gap="xs">
              <Text fw={500}>Articoli</Text>
              <Badge variant="light" color="blue" size="sm">
                {form.values.articles.length}
              </Badge>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              {/* Import da Excel */}
              <ExcelArticleImport onImport={handleExcelImport} />

              {/* Lista articoli attuali */}
              {form.values.articles.map((article, index) => (
                <Card key={index} withBorder padding="sm">
                  <Group justify="space-between">
                    <Group>
                      <IconPackage size={16} />
                      <div>
                        <Text fw={500} size="sm">{article.articleName}</Text>
                        <Text size="xs" c="dimmed">
                          Codice: {article.articleCode} • Quantità: {article.quantity}
                        </Text>
                      </div>
                    </Group>
                    <Button
                      variant="subtle"
                      color="red"
                      size="xs"
                      onClick={() => handleArticleRemove(index)}
                    >
                      <IconX size={14} />
                    </Button>
                  </Group>
                </Card>
              ))}

              {form.values.articles.length === 0 && (
                <Alert color="yellow" variant="light">
                  <Text size="sm">Nessun articolo presente</Text>
                </Alert>
              )}

              {/* Aggiunta nuovo articolo */}
              <ArticleSelector
                onSelect={handleArticleAdd}
                onCancel={() => {}}
                clientId={selectedClient?.clientId}
              />
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {/* Pulsanti */}
      <Group justify="flex-end" mt="md">
        <Button variant="light" onClick={handleClose}>
          Annulla
        </Button>
        <Button
          type="button"
          loading={updateMutation.isPending}
          onClick={() => handleSubmit(form.values)}
        >
          Salva Modifiche
        </Button>
      </Group>
    </Stack>
  );

  // Render dello stepper per modalità create
  const renderCreateStepper = () => (
    <Stack gap="md">
      {/* Stepper */}
      <Stepper active={currentStep} onStepClick={setCurrentStep} size="sm">
        <Stepper.Step
          label="Dati Base"
          description="Info generali"
          icon={<IconFileText size={16} />}
        />
        <Stepper.Step
          label="Cliente"
          description="Selezione cliente"
          icon={<IconUser size={16} />}
        />
        <Stepper.Step
          label="Articoli"
          description="Gestione articoli"
          icon={<IconPackage size={16} />}
        />
        <Stepper.Step
          label="Dettagli"
          description="Finalizzazione"
          icon={<IconCalendar size={16} />}
        />
      </Stepper>

      {/* Step Content */}
      {currentStep === 0 && (
        <Stack gap="md">
          <Text fw={500} size="lg">Informazioni Generali</Text>

          <TextInput
            label="Titolo"
            placeholder="Inserisci il titolo della richiesta..."
            required
            {...form.getInputProps('title')}
          />

          <Select
            label="Tipo Richiesta"
            data={[
              { value: '1', label: 'Bozzetto' },
              { value: '2', label: 'Campione' },
              { value: '3', label: 'Produzione' },
              { value: '4', label: 'Gamma' }
            ]}
            {...form.getInputProps('requestType')}
            onChange={(value) => {
              form.setFieldValue('requestType', value || '1');
              // Reset progressive number quando cambia il tipo
              form.setFieldValue('progressiveNumber', null);
              setProgressiveNumberError(null);
              setTimeout(() => refetchSuggestion(), 100);
            }}
          />

          {/* Numero Progressivo */}
          <Card withBorder padding="sm" bg="gray.0">
            <Text size="sm" fw={500} mb="xs">
              <Group gap="xs">
                <IconHash size={16} />
                Numero Progressivo
              </Group>
            </Text>
            <Grid>
              <Grid.Col span={5}>
                <NumberInput
                  label="Numero"
                  placeholder={isLoadingSuggestion ? 'Caricamento...' : 'N'}
                  min={1}
                  value={form.values.progressiveNumber ?? undefined}
                  onChange={(value) => {
                    form.setFieldValue('progressiveNumber', value as number ?? null);
                    setProgressiveNumberError(null);
                  }}
                  onBlur={handleProgressiveNumberBlur}
                  error={progressiveNumberError}
                  rightSection={isValidating ? <IconRefresh size={14} className="animate-spin" /> : null}
                />
              </Grid.Col>
              <Grid.Col span={1}>
                <Text size="xl" ta="center" pt="xl">/</Text>
              </Grid.Col>
              <Grid.Col span={4}>
                <NumberInput
                  label="Anno"
                  placeholder="AAAA"
                  min={2020}
                  max={2100}
                  {...form.getInputProps('progressiveYear')}
                  onChange={(value) => {
                    form.setFieldValue('progressiveYear', value as number);
                    setProgressiveNumberError(null);
                  }}
                  onBlur={handleProgressiveNumberBlur}
                />
              </Grid.Col>
              <Grid.Col span={2}>
                <Stack gap={4} pt="xl">
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={() => {
                      refetchSuggestion();
                      if (suggestion) {
                        form.setFieldValue('progressiveNumber', suggestion.number);
                        form.setFieldValue('progressiveYear', suggestion.year);
                        setProgressiveNumberError(null);
                      }
                    }}
                    loading={isLoadingSuggestion}
                    leftSection={<IconRefresh size={14} />}
                  >
                    Auto
                  </Button>
                </Stack>
              </Grid.Col>
            </Grid>
            {suggestion && !progressiveNumberError && (
              <Text size="xs" c="dimmed" mt="xs">
                Suggerimento: {suggestion.formatted} (prossimo disponibile)
              </Text>
            )}
            {progressiveNumberError && (
              <Alert color="red" variant="light" mt="xs" p="xs">
                <Text size="xs">{progressiveNumberError}</Text>
              </Alert>
            )}
          </Card>
        </Stack>
      )}

      {currentStep === 1 && (
        <Stack gap="md">
          <Text fw={500} size="lg">Selezione Cliente</Text>

          <ClientSelector
            value={selectedClient || undefined}
            onChange={handleClientSelect}
            required
          />

          {selectedClient && (
            <Alert color="green" variant="light">
              <Group>
                <IconCheck size={16} />
                <Text size="sm">
                  Cliente selezionato: <strong>{selectedClient.clientName}</strong>
                </Text>
              </Group>
            </Alert>
          )}
        </Stack>
      )}

      {currentStep === 2 && (
        <Stack gap="md">
          <Group justify="space-between">
            <Text fw={500} size="lg">Gestione Articoli</Text>
            <Badge variant="light" color="blue">
              {form.values.articles.length} articol{form.values.articles.length !== 1 ? 'i' : 'o'}
            </Badge>
          </Group>

          {/* Import da Excel */}
          <ExcelArticleImport onImport={handleExcelImport} />

          {/* Lista articoli attuali */}
          {form.values.articles.map((article, index) => (
            <Card key={index} withBorder padding="sm">
              <Group justify="space-between">
                <Group>
                  <IconPackage size={16} />
                  <div>
                    <Text fw={500} size="sm">{article.articleName}</Text>
                    <Text size="xs" c="dimmed">
                      Codice: {article.articleCode} • Quantità: {article.quantity}
                    </Text>
                  </div>
                </Group>
                <Button
                  variant="subtle"
                  color="red"
                  size="xs"
                  onClick={() => handleArticleRemove(index)}
                >
                  <IconX size={14} />
                </Button>
              </Group>
            </Card>
          ))}

          {/* Aggiunta nuovo articolo */}
          <ArticleSelector
            onSelect={handleArticleAdd}
            onCancel={() => {}}
            clientId={selectedClient?.clientId}
          />

          {form.values.articles.length === 0 && (
            <Alert color="yellow" variant="light">
              <Text size="sm">Aggiungi almeno un articolo per continuare</Text>
            </Alert>
          )}
        </Stack>
      )}

      {currentStep === 3 && (
        <Stack gap="md">
          <Text fw={500} size="lg">Dettagli Aggiuntivi</Text>

          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <DatePickerInput
                label="Data Scadenza"
                placeholder="Seleziona data..."
                {...form.getInputProps('dueDate')}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <NumberInput
                label="Ore Stimate"
                placeholder="0"
                min={0}
                {...form.getInputProps('estimatedHours')}
              />
            </Grid.Col>
          </Grid>

          <TextInput
            label="Tags"
            placeholder="tag1, tag2, tag3..."
            description="Separare i tag con virgole"
            {...form.getInputProps('tags')}
          />

          <Textarea
            label="Note"
            placeholder="Note aggiuntive..."
            minRows={3}
            {...form.getInputProps('notes')}
          />

          {/* Riepilogo */}
          <Card withBorder padding="md" bg="gray.0">
            <Text fw={500} mb="sm">Riepilogo Richiesta</Text>
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">N. Progressivo:</Text>
                <Text size="sm" fw={500}>
                  {form.values.progressiveNumber}/{form.values.progressiveYear}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Cliente:</Text>
                <Text size="sm" fw={500}>{form.values.clientName}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Tipo Richiesta:</Text>
                <Text size="sm" fw={500}>
                  {form.values.requestType === '1' ? 'Bozzetto' :
                   form.values.requestType === '2' ? 'Campione' :
                   form.values.requestType === '3' ? 'Produzione' :
                   form.values.requestType === '4' ? 'Gamma' : 'Altro'}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Articoli:</Text>
                <Text size="sm" fw={500}>{form.values.articles.length}</Text>
              </Group>
              {form.values.deliveryDate && (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Data di Consegna:</Text>
                  <Text size="sm" fw={500}>
                    {form.values.deliveryDate.toLocaleDateString('it-IT')}
                  </Text>
                </Group>
              )}
            </Stack>
          </Card>
        </Stack>
      )}

      {/* Navigation */}
      <Group justify="space-between" mt="xl">
        <Group>
          {currentStep > 0 && (
            <Button variant="light" onClick={prevStep}>
              Indietro
            </Button>
          )}
        </Group>

        <Group>
          <Button variant="light" onClick={handleClose}>
            Annulla
          </Button>

          {currentStep < 3 ? (
            <Button
              onClick={nextStep}
              disabled={!canProceedToNextStep()}
            >
              Avanti
            </Button>
          ) : (
            <Button
              type="button"
              loading={createMutation.isPending || updateMutation.isPending}
              onClick={() => handleSubmit(form.values)}
            >
              Crea Richiesta
            </Button>
          )}
        </Group>
      </Group>
    </Stack>
  );

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={mode === 'create' ? 'Nuova Richiesta' : 'Modifica Richiesta'}
      size="xl"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        {mode === 'edit' ? renderEditForm() : renderCreateStepper()}
      </form>
    </Modal>
  );
}
